import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  QueryList,
  Renderer2,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { ScreenType } from '@fgb/portal-component-library/src/lib/directives';
import { FeedQuery, FeedService, LoyaltyFeed } from '@fgb/core';
import { LoyaltyFeedItemComponent } from '../loyalty-feed-item/loyalty-feed-item.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import gsap from 'gsap';
import { InertiaPlugin } from 'gsap/InertiaPlugin';
import Draggable from 'gsap/Draggable';
import { Flip } from 'gsap/Flip';
import { animate, style, transition, trigger } from '@angular/animations';
import { firstValueFrom, Observable } from 'rxjs';

gsap.registerPlugin(Draggable);
gsap.registerPlugin(InertiaPlugin);
gsap.registerPlugin(Flip);

@Component({
  selector: 'fgbcl-loyalty-feed-list',
  templateUrl: './loyalty-feed-list.component.html',
  styleUrl: './loyalty-feed-list.component.scss',
  animations: [
    trigger('popFade', [
      transition(':enter', [
        style({
          opacity: 0,
          transform: 'scale(0.85)',
        }),
        animate(
          '420ms cubic-bezier(0.22, 1, 0.36, 1)',
          style({
            opacity: 1,
            transform: 'scale(1)',
          })
        ),
      ]),
      transition(':leave', [
        animate(
          '200ms ease-in',
          style({
            opacity: 0,
            transform: 'scale(0.98)',
          })
        ),
      ]),
    ]),
  ],
})
export class LoyaltyFeedListComponent implements AfterViewInit, OnInit, OnDestroy {
  @ViewChild('list', { static: false, read: ElementRef }) feedList!: ElementRef;
  @ViewChild('container', { static: false }) container!: ElementRef<HTMLElement>;
  @ViewChildren(LoyaltyFeedItemComponent, { read: ElementRef }) feedItems!: QueryList<ElementRef<HTMLElement>>;
  @ViewChild('activeDot', { static: false }) activeDot!: ElementRef<HTMLDivElement>;
  isVertical: boolean = false;
  currentIndex = 0;
  dotProgress: number = 0;
  dots: any[] = [];
  screenType = ScreenType;
  showDots: boolean = true;
  marginRight: number = 0;
  dynamicLoyaltyFeeds$: Observable<LoyaltyFeed[]>;
  elements: ElementRef<HTMLDivElement>[];
  private mm = gsap.matchMedia();
  private flipAnimation?: gsap.core.Animation;
  private renderer = inject(Renderer2);
  private cd = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);
  private draggable!: Draggable;
  private feedQuery = inject(FeedQuery);
  private feedService = inject(FeedService);
  private isAnimating = false;

  /* ================================
   * Lifecyle Hooks
   * ================================ */

  ngOnInit(): void {
    firstValueFrom(this.feedService.fetchDynamicLoyaltyFeed());
    this.dynamicLoyaltyFeeds$ = this.feedQuery.dynamicLoyaltyFeeds$;
  }

  ngAfterViewInit(): void {
    // Initialize only for mobile, as this was designed to work only for mobile.
    this.mm.add('(max-width: 991px)', () => {
      this._init();
      const subscription = this.feedItems.changes.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
        this.reinitialize();
      });
      return () => {
        this.draggable?.kill();
        subscription.unsubscribe();
      };
    });
  }

  ngOnDestroy(): void {
    this.mm.kill();
  }

  /* ================================
   * Public Methods
   * ================================ */

  toggleLayout(): void {
    if (this.isAnimating) return;
    this.isAnimating = true;

    this.showDots = false;
    const itemEls = this.feedItems.map((i) => i?.nativeElement);

    // Step 1: fade items out smoothly before any layout change
    gsap.to(itemEls, {
      opacity: 0,
      duration: 0.18,
      ease: 'power1.in',
      onComplete: () => {
        if (this.flipAnimation) {
          this.flipAnimation.kill();
          Flip.killFlipsOf('.dynamic-feed-list, fgbcl-loyalty-feed-item');
        }

        // Step 2: capture state, then apply DOM change
        const state = Flip.getState('.dynamic-feed-list, fgbcl-loyalty-feed-item');
        this.isVertical = !this.isVertical;
        this.cd.detectChanges();

        // Step 3: animate layout transition
        this.flipAnimation = Flip.from(state, {
          duration: 0.55,
          ease: 'power2.inOut',
          props: 'width,height',
          nested: true,
          onComplete: () => {
            this.showDots = true;
            this.isAnimating = false;

            if (this.isVertical) {
              const allItems = this.feedItems.map((el) => el?.nativeElement);
              gsap.set(allItems, { opacity: 1 });
              const animateItems = allItems.slice(0, 3);

              gsap.killTweensOf(animateItems);
              gsap.fromTo(
                animateItems,
                { opacity: 0, y: 30 },
                {
                  opacity: 1,
                  y: 0,
                  duration: 0.7,
                  stagger: 0.12,
                  ease: 'power3.out',
                }
              );
            } else {
              this.updateVisible();
              gsap.killTweensOf(itemEls);

              // Restore opacity on off-screen items immediately so they aren't blank when scrolled to
              const nonVisibleEls = this.feedItems
                .filter((el) => !el?.nativeElement.classList.contains('visible'))
                .map((el) => el?.nativeElement);
              gsap.set(nonVisibleEls, { opacity: 1, scale: 1 });

              const visibleItem = this.feedItems.find((el) => el?.nativeElement.classList.contains('visible'));
              if (visibleItem) {
                gsap.fromTo(
                  visibleItem?.nativeElement,
                  { opacity: 0, scale: 0.85 },
                  {
                    opacity: 1,
                    scale: 1,
                    duration: 0.55,
                    ease: 'back.out(1.7)',
                  }
                );
              }
            }
            this.cd.detectChanges();
          },
        });
      },
    });
  }

  next(): void {
    const itemWidth = this.feedItems.first.nativeElement.clientWidth + this.marginRight; // item width + margin
    this.currentIndex = Math.min(this.currentIndex + 1, this.feedItems.length - 1);

    gsap.to(this.feedList.nativeElement, {
      x: -this.currentIndex * itemWidth,
      duration: 0.5,
      onUpdate: () => {
        this.draggable.update();
      },
    });

    this.updateDotProgress();
  }

  previous(): void {
    const itemWidth = this.feedItems.first.nativeElement.clientWidth + this.marginRight; // item width + margin
    this.currentIndex = Math.max(this.currentIndex - 1, 0);

    gsap.to(this.feedList.nativeElement, {
      x: -this.currentIndex * itemWidth,
      duration: 0.5,
      onUpdate: () => {
        this.draggable.update();
      },
    });
    this.updateDotProgress();
  }

  /* ================================
   * Private Methods
   * ================================ */

  private _init(): void {
    const container = this.container?.nativeElement;
    const list = this.feedList?.nativeElement;

    if (!list || !container) return;
    this.dots = Array.from({ length: this.feedItems.length });

    const firstItem = list.querySelector('fgbcl-loyalty-feed-item') as HTMLElement;
    if (!firstItem) return; // no items
    const style = getComputedStyle(firstItem);
    this.marginRight = parseFloat(style.marginRight) || 0;
    const itemFullWidth = firstItem.offsetWidth + this.marginRight;

    // Max negative translateX to prevent over-dragging
    const maxDrag = 0;
    const minDrag = container.offsetWidth - list.scrollWidth - 3;

    setTimeout(() => {
      this.draggable = Draggable.create(list, {
        type: 'x',
        edgeResistance: 0.65, // resistance at edges
        bounds: { minX: minDrag, maxX: maxDrag },
        inertia: true,
        throwProps: true, // inertia plugin (throw)
        snap: (x) => {
          const snapped = Math.round(x / itemFullWidth) * itemFullWidth;
          return gsap.utils.clamp(minDrag, maxDrag, snapped);
        },
        cursor: 'grab',
        onPress() {
          this.target.style.cursor = 'grabbing';
        },
        onRelease() {
          this.target.style.cursor = 'grab';
        },
        onDrag: () => this.updateVisible(),
        onThrowUpdate: () => this.updateVisible(),
      })[0]; // Draggable.create returns an array
    });

    this.updateDotProgress();
  }

  private reinitialize() {
    if (!this.feedList?.nativeElement) return;

    if (this.draggable) {
      this.draggable.kill();
    }

    gsap.set(this.feedList.nativeElement, {
      x: -this.currentIndex * (this.feedItems.first.nativeElement.clientWidth + this.marginRight),
    });
    this._init();
    this.cd.detectChanges();
  }

  private updateDotProgress(): void {
    const length = this.feedItems.length - 2;
    this.dotProgress = Math.round(Math.min(100, Math.max(10, ((this.currentIndex + 1) / length) * 100)));
    this.cd.detectChanges();
  }

  private updateVisible(): void {
    const container = this.feedList.nativeElement.parentElement.getBoundingClientRect();
    this.feedItems.forEach((elRef, idx) => {
      const el = elRef.nativeElement;
      const rect = el.getBoundingClientRect();
      const inView = rect.left < container.right && rect.right > container.left;
      if (inView) {
        this.renderer.addClass(el, 'visible');
        if (window.innerWidth <= 992) {
          this.currentIndex = idx;
          this.updateDotProgress();
          return;
        }
        this.currentIndex = idx - 1;
        this.updateDotProgress();
      } else {
        this.renderer.removeClass(el, 'visible');
      }
    });
    this.cd.detectChanges();
  }
}
